/**
 * Error Handling Service
 * 
 * A centralized service for handling, logging, and reporting errors throughout the application.
 * This service ensures consistent error handling and prevents sensitive information leakage.
 */

import { ErrorSeverity, ErrorCategory, mapFirebaseErrorToCategory, mapFirebaseErrorToSeverity } from './errorTypes';
import { sanitizeError, sanitizeFirebaseAuthError } from '../errorSanitizer';

// Configuration for error handling
const config = {
  // Whether to log errors to the console
  enableConsoleLogging: true,

  // Whether to send errors to a remote logging service
  enableRemoteLogging: false,

  // Remote logging endpoint (could be loaded from environment variables)
  remoteLoggingEndpoint: null,

  // Whether to include stack traces in logs (never in user-facing messages)
  includeStackTraces: true,

  // Whether to include user context in logs (e.g., user ID, not PII)
  includeUserContext: true
};

// User context that can be set by the application
let userContext = {
  userId: null,
  isAuthenticated: false,
  sessionId: null,
  // Don't store PII (personally identifiable information) here
};

/**
 * Set the current user context for error logging
 * 
 * @param {Object} context - User context information
 */
export const setUserContext = (context) => {
  userContext = {
    ...userContext,
    ...context
  };
};

/**
 * Clear the user context (e.g., on logout)
 */
export const clearUserContext = () => {
  userContext = {
    userId: null,
    isAuthenticated: false,
    sessionId: null
  };
};

/**
 * Format an error for logging
 * 
 * @param {Error|string} error - The error object or message
 * @param {Object} options - Additional options for error formatting
 * @returns {Object} Formatted error object for logging
 */
const formatErrorForLogging = (error, options = {}) => {
  const {
    category = ErrorCategory.UNKNOWN,
    severity = ErrorSeverity.ERROR,
    context = {},
    source = 'client'
  } = options;

  // Basic error info
  const errorInfo = {
    timestamp: new Date().toISOString(),
    message: error.message || error.toString(),
    category,
    severity,
    source,
    context: {
      ...context,
      url: window.location.href,
      userAgent: navigator.userAgent
    }
  };

  // Add Firebase error code if available
  if (error.code) {
    errorInfo.errorCode = error.code;
  }

  // Add stack trace if available and enabled
  if (config.includeStackTraces && error.stack) {
    errorInfo.stack = error.stack;
  }

  // Add user context if enabled
  if (config.includeUserContext && userContext.userId) {
    errorInfo.context.user = {
      userId: userContext.userId,
      isAuthenticated: userContext.isAuthenticated,
      sessionId: userContext.sessionId
    };
  }

  return errorInfo;
};

/**
 * Log an error to the console
 * 
 * @param {Object} errorInfo - Formatted error information
 * @param {ErrorSeverity} severity - Error severity level
 */
const logToConsole = (errorInfo, severity) => {
  if (!config.enableConsoleLogging) return;

  const { category, message, errorCode, stack } = errorInfo;

  // Use appropriate console method based on severity
  switch (severity) {
    case ErrorSeverity.INFO:
      console.info(`[${category}] ${message}`, errorCode ? { code: errorCode } : '');
      break;
    case ErrorSeverity.WARNING:
      console.warn(`[${category}] ${message}`, errorCode ? { code: errorCode } : '');
      break;
    case ErrorSeverity.CRITICAL:
      console.error(`[CRITICAL] [${category}] ${message}`, errorCode ? { code: errorCode } : '', stack || '');
      break;
    case ErrorSeverity.ERROR:
    default:
      console.error(`[${category}] ${message}`, errorCode ? { code: errorCode } : '', stack || '');
      break;
  }
};

/**
 * Send error to a remote logging service
 * 
 * @param {Object} errorInfo - Formatted error information
 * @returns {Promise<void>}
 */
const logToRemoteService = async (errorInfo) => {
  if (!config.enableRemoteLogging || !config.remoteLoggingEndpoint) return;

  try {
    // Remove sensitive data before sending
    const safeErrorInfo = { ...errorInfo };

    // Don't send stack traces to remote service unless it's a controlled environment
    delete safeErrorInfo.stack;

    // Send error to remote logging service
    await fetch(config.remoteLoggingEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(safeErrorInfo),
      // Don't include credentials by default for security
      credentials: 'omit'
    });
  } catch (err) {
    // If remote logging fails, log to console but don't throw
    console.error('Failed to send error to remote logging service:', err);
  }
};

/**
 * Handle an error by logging it and returning a sanitized message
 * 
 * @param {Error|string} error - The error object or message
 * @param {Object} options - Additional options for error handling
 * @returns {string} Sanitized error message safe for user display
 */
export const handleError = (error, options = {}) => {
  // If no error, return empty string
  if (!error) return '';

  // Determine error category and severity
  let category = options.category || ErrorCategory.UNKNOWN;
  let severity = options.severity || ErrorSeverity.ERROR;

  // For Firebase errors, map to our internal categories and severity
  if (error.code) {
    category = options.category || mapFirebaseErrorToCategory(error.code);
    severity = options.severity || mapFirebaseErrorToSeverity(error.code);
  }

  // Format error for logging
  const errorInfo = formatErrorForLogging(error, {
    category,
    severity,
    context: options.context || {},
    source: options.source || 'client'
  });

  // Log to console
  logToConsole(errorInfo, severity);

  // Log to remote service for non-info errors
  if (severity !== ErrorSeverity.INFO) {
    logToRemoteService(errorInfo);
  }

  // Return sanitized message for user display
  if (error.code && error.code.startsWith('auth/')) {
    return sanitizeFirebaseAuthError(error);
  }

  return sanitizeError(error);
};

/**
 * Create an error response object with a sanitized message
 * 
 * @param {Error|string} error - The error object or message
 * @param {Object} options - Additional options for error handling
 * @returns {Object} Error response object with sanitized message
 */
export const createErrorResponse = (error, options = {}) => {
  return {
    error: handleError(error, options)
  };
};

/**
 * Configure the error handling service
 * 
 * @param {Object} newConfig - New configuration options
 */
export const configureErrorService = (newConfig) => {
  Object.assign(config, newConfig);
};

/**
 * Create a wrapped version of a function that handles errors
 * 
 * @param {Function} fn - The function to wrap
 * @param {Object} options - Error handling options
 * @returns {Function} Wrapped function with error handling
 */
export const withErrorHandling = (fn, options = {}) => {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      handleError(error, options);
      throw error; // Re-throw to allow caller to handle
    }
  };
};

/**
 * Initialize the error handling service
 * 
 * @param {Object} initialConfig - Initial configuration
 */
export const initErrorService = (initialConfig = {}) => {
  // Apply initial configuration
  configureErrorService(initialConfig);

  // Set up global error handler for uncaught exceptions
  window.addEventListener('error', (event) => {
    handleError(event.error || new Error(event.message), {
      category: ErrorCategory.UNKNOWN,
      severity: ErrorSeverity.CRITICAL,
      context: {
        lineNo: event.lineno,
        colNo: event.colno,
        filename: event.filename
      }
    });
  });

  // Set up handler for unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    handleError(event.reason || new Error('Unhandled Promise rejection'), {
      category: ErrorCategory.UNKNOWN,
      severity: ErrorSeverity.ERROR
    });
  });

  console.log('Error handling service initialized');
};

export default {
  handleError,
  createErrorResponse,
  setUserContext,
  clearUserContext,
  configureErrorService,
  withErrorHandling,
  initErrorService
};
